import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PickPoints } from 'config/analysisConfig';
import { useConfig } from 'config/useConfig';
import type { Label } from 'config/config';
import { getPolyPoints } from 'utils/CPRUtilities/markerLineUtils';
import type { PerpendicularLine } from 'utils/CPRUtilities/markerLineTypes';
import { usePanelContainer } from '../usePanelContainer';
import { scheduleRender } from '../renderState';
import {
  buildPickPointFromCrossSectionAsset,
  findNearestCprCenterlinePathIndex,
  getCrossSectionImageAsset,
  type CprCenterlinePathPoint,
  type CprCrossSectionImages,
  type CprMode,
  type CprOverlayGeometry,
} from '../cprImageResources';
import { handleNodeChange } from '../Renderers/vtkManagers';
import {
  buildCprIndexBandOverlay,
  buildCprOverlayPerpendicularLines,
  nearestCprPathArrayIndex,
  projectCprCenterlinePathToViewport,
} from './cprImageOverlayGeometry';
import type { ImageLayerStackViewportContext } from '../ImageLayerStack';

type CprImageMarkerOverlayProps = {
  width: number;
  height: number;
  mode: CprMode;
  mainIndex: number;
  branchId: number;
  pickPoints: PickPoints[];
  numPairs?: number;
  centerlinePath: CprCenterlinePathPoint[];
  overlayGeometry?: CprOverlayGeometry;
  imageManifest?: CprCrossSectionImages | null;
  centerlineData?: any;
  viewportContext?: ImageLayerStackViewportContext;
  hidePlaqueOutline?: boolean;
  hidePlaqueLesion?: boolean;
  stackMarkerIndexes?: number[];
  isServerImageMode?: boolean;
  overlayLabel?: Partial<Label>;
};

function toMarkerPoint(
  centerlinePath: CprCenterlinePathPoint[],
  index: number,
) {
  if (!centerlinePath.length) return null;
  const exact = centerlinePath.find((point) =>
    getPathPointCenterlineIndexCandidates(point).includes(index),
  );
  if (exact) return exact;

  return centerlinePath.reduce(
    (best, point) =>
      centerlineIndexDistance(point, index) <
      centerlineIndexDistance(best, index)
        ? point
        : best,
    centerlinePath[0],
  );
}

function getPathPointCenterlineIndexCandidates(
  point: CprCenterlinePathPoint,
) {
  return [point.sourceCenterlineIndex, point.index].filter(
    (value): value is number => Number.isFinite(value),
  );
}

function getPathPointCenterlineIndex(point: CprCenterlinePathPoint) {
  return Number.isFinite(point.sourceCenterlineIndex)
    ? point.sourceCenterlineIndex!
    : point.index;
}

function centerlineIndexDistance(
  point: CprCenterlinePathPoint,
  index: number,
) {
  const candidates = getPathPointCenterlineIndexCandidates(point);
  if (!candidates.length) return Number.POSITIVE_INFINITY;
  return Math.min(...candidates.map((candidate) => Math.abs(candidate - index)));
}

function lineMidpoint(line: PerpendicularLine) {
  return {
    x: (line.startX + line.endX) / 2,
    y: (line.startY + line.endY) / 2,
  };
}

function lineUnit(line: PerpendicularLine) {
  const dx = line.endX - line.startX;
  const dy = line.endY - line.startY;
  const length = Math.hypot(dx, dy) || 1;
  return {
    length,
    offsetX: dx / length,
    offsetY: dy / length,
  };
}

function markerLineStyle({
  markerOffset,
  numPairs,
  config,
}: {
  markerOffset: number;
  numPairs: number;
  config: any;
}) {
  let stroke = config.colors.Stenosis;
  const hitWidth =
    markerOffset % numPairs === 0 || markerOffset % numPairs === numPairs - 1
      ? 10
      : 5;

  if (numPairs !== 1) {
    const remainder = markerOffset % numPairs;
    if (remainder === 0) stroke = config.colors.Proximal;
    else if (remainder === numPairs - 1) stroke = config.colors.Distal;
  }

  return { stroke, hitWidth };
}

function polygonStringToPoints(
  points: string,
): Array<{ x: number; y: number }> {
  return points
    .trim()
    .split(/\s+/)
    .map((pair) => {
      const [x, y] = pair.split(',').map(Number);
      return { x, y };
    })
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
}

function pointsToPolygonString(points: Array<{ x: number; y: number }>) {
  return points.map((point) => `${point.x},${point.y}`).join(' ');
}

function cprSyncNow() {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}


function smoothOpenPolyline(
  points: Array<{ x: number; y: number }>,
  iterations: number,
) {
  if (points.length < 4) return points;
  let current = points.map((point) => ({ ...point }));
  for (let iteration = 0; iteration < iterations; iteration += 1) {
    current = current.map((point, index) => {
      if (index === 0 || index === current.length - 1) return point;
      const prev = current[index - 1];
      const next = current[index + 1];
      return {
        x: prev.x * 0.25 + point.x * 0.5 + next.x * 0.25,
        y: prev.y * 0.25 + point.y * 0.5 + next.y * 0.25,
      };
    });
  }
  return current;
}

function smoothMarkerAreaPolygon(points: string) {
  const polygonPoints = polygonStringToPoints(points);
  if (polygonPoints.length < 8 || polygonPoints.length % 2 !== 0) {
    return points;
  }

  const half = polygonPoints.length / 2;
  const startEdge = polygonPoints.slice(0, half);
  const endEdge = polygonPoints.slice(half).reverse();
  const iterations = Math.min(4, Math.max(2, Math.floor(half / 18)));
  const smoothedStart = smoothOpenPolyline(startEdge, iterations);
  const smoothedEnd = smoothOpenPolyline(endEdge, iterations);

  return pointsToPolygonString([
    ...smoothedStart,
    ...smoothedEnd.reverse(),
  ]);
}


function pointsToPathD(
  points: Array<{ x: number; y: number }>,
  closed?: boolean,
) {
  if (points.length < 2) return '';
  const [first, ...rest] = points;
  const lineCommands = rest.map((point) => `L ${point.x} ${point.y}`);
  return [`M ${first.x} ${first.y}`, ...lineCommands, closed ? 'Z' : '']
    .filter(Boolean)
    .join(' ');
}

function outlineStrokeForType(type: string | undefined, config: any) {
  const normalized = type?.toLowerCase() ?? '';
  if (normalized.includes('wall')) {
    return config.colors?.walloutline ?? '#d87409';
  }
  if (normalized.includes('plaque')) {
    return config.colors?.nonCalcified ?? '#00d0ff';
  }
  return config.colors?.lumenoutline ?? config.colors?.LUMEN ?? '#d4d438';
}

function hasWorldPickPoint(point: PickPoints) {
  return Array.isArray(point.position) && Array.isArray(point.normal);
}

function clampMiddleIndexBetweenBounds(
  candidate: number,
  proximalIndex: number,
  distalIndex: number,
) {
  const proximal = Math.round(proximalIndex);
  const distal = Math.round(distalIndex);
  const min = Math.min(proximal, distal);
  const max = Math.max(proximal, distal);

  if (max - min > 1) {
    return Math.max(min + 1, Math.min(max - 1, Math.round(candidate)));
  }

  return Math.max(min, Math.min(max, Math.round(candidate)));
}

function nearestCprPathArrayIndexByPoint(
  path: CprCenterlinePathPoint[],
  point: { x: number; y: number },
  mode: 'xy' | 'y' = 'xy',
): number | null {
  if (!path.length) return null;
  let bestIndex = -1;
  let bestDistance = Number.POSITIVE_INFINITY;
  path.forEach((pathPoint, index) => {
    const dy = pathPoint.y - point.y;
    const distance = mode === 'y'
      ? Math.abs(dy)
      : (pathPoint.x - point.x) * (pathPoint.x - point.x) + dy * dy;
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = index;
    }
  });
  return bestIndex >= 0 ? bestIndex : null;
}

function nearestCprPathIndexByPoint(
  path: CprCenterlinePathPoint[],
  point: { x: number; y: number },
  mode: 'xy' | 'y' = 'xy',
): number | null {
  if (mode !== 'y') {
    return findNearestCprCenterlinePathIndex(path, point);
  }

  const arrayIndex = nearestCprPathArrayIndexByPoint(path, point, 'y');
  const pathPoint = arrayIndex === null ? null : path[arrayIndex];
  return pathPoint && Number.isFinite(getPathPointCenterlineIndex(pathPoint))
    ? getPathPointCenterlineIndex(pathPoint)
    : null;
}

export default function CprImageMarkerOverlay({
  width,
  height,
  mode,
  mainIndex,
  branchId,
  pickPoints,
  numPairs = 1,
  centerlinePath,
  overlayGeometry,
  imageManifest,
  centerlineData,
  viewportContext,
  hidePlaqueOutline = false,
  hidePlaqueLesion = false,
  stackMarkerIndexes,
  isServerImageMode = false,
  overlayLabel,
}: CprImageMarkerOverlayProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const dragIndexRef = useRef<number | null>(null);
  const dragGroupRef = useRef<{
    indices: number[];
    startPathArrayIndex: number;
  } | null>(null);
  const pickPointIndexesRef = useRef<number[]>(pickPoints.map((p) => p.index));
  const [livePickPointIndexes, setLivePickPointIndexes] = useState<
    number[] | null
  >(null);
  const livePickPointIndexesAnimationFrameRef = useRef<number | null>(null);
  const pendingLivePickPointIndexesRef = useRef<number[] | null>(null);
  const [hoveredPolygonIndex, setHoveredPolygonIndex] = useState<number | null>(
    null,
  );
  const { config, setData } = useConfig();
  const label = overlayLabel ?? config.label;
  const { analysisType, updateCPRState } = usePanelContainer();
  const isAorticMode = analysisType === 'aorticValveCalcium';
  const shouldRenderPlaqueOutline = analysisType === 'plaque';
  const viewportWidth = Math.max(1, viewportContext?.containerWidth ?? width);
  const viewportHeight = Math.max(
    1,
    viewportContext?.containerHeight ?? height,
  );
  const indexPickMode = overlayGeometry?.nearestIndexMode === 'y' ? 'y' : 'xy';
  const propPickPointIndexes = useMemo(
    () => pickPoints.map((point) => point.index),
    [pickPoints],
  );
  const displayPickPointIndexes = livePickPointIndexes ?? propPickPointIndexes;
  const isStackMarkerMode = !!stackMarkerIndexes?.length;
  const isServerStackMarkerMode =
    analysisType === 'plaque' &&
    isStackMarkerMode &&
    isServerImageMode;
  const imageToSvgPoint = (point: { x: number; y: number }) =>
    viewportContext?.imageToViewportPoint(point) ?? point;
  const toSvgPolygon = (points: string) =>
    pointsToPolygonString(
      polygonStringToPoints(points).map((point) => imageToSvgPoint(point)),
    );
  const lineToSvg = (line: PerpendicularLine): PerpendicularLine => {
    const start = imageToSvgPoint({ x: line.startX, y: line.startY });
    const end = imageToSvgPoint({ x: line.endX, y: line.endY });
    return {
      startX: start.x,
      startY: start.y,
      endX: end.x,
      endY: end.y,
    };
  };

  if (
    dragIndexRef.current === null &&
    dragGroupRef.current === null &&
    livePickPointIndexes === null
  ) {
    pickPointIndexesRef.current = propPickPointIndexes;
  }

  useEffect(
    () => () => {
      if (livePickPointIndexesAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(
          livePickPointIndexesAnimationFrameRef.current,
        );
        livePickPointIndexesAnimationFrameRef.current = null;
      }
      pendingLivePickPointIndexesRef.current = null;
    },
    [],
  );

  const scheduleLivePickPointIndexes = (indexes: number[]) => {
    pendingLivePickPointIndexesRef.current = indexes.slice();
    if (livePickPointIndexesAnimationFrameRef.current !== null) return;
    livePickPointIndexesAnimationFrameRef.current = window.requestAnimationFrame(
      () => {
        livePickPointIndexesAnimationFrameRef.current = null;
        const pending = pendingLivePickPointIndexesRef.current;
        pendingLivePickPointIndexesRef.current = null;
        if (pending) setLivePickPointIndexes(pending);
      },
    );
  };

  const flushLivePickPointIndexes = () => {
    if (livePickPointIndexesAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(livePickPointIndexesAnimationFrameRef.current);
      livePickPointIndexesAnimationFrameRef.current = null;
    }
    const pending = pendingLivePickPointIndexesRef.current;
    pendingLivePickPointIndexesRef.current = null;
    if (pending) setLivePickPointIndexes(pending);
  };

  const displayCenterlinePath = useMemo(
    () => projectCprCenterlinePathToViewport(centerlinePath, imageToSvgPoint),
    [centerlinePath, viewportContext],
  );
  const bandWidth = isAorticMode ? 100 : 20;
  const imagePerpendicularLines = useMemo(
    () =>
      buildCprOverlayPerpendicularLines({
        centerlinePath,
        overlayGeometry,
        fallbackBandWidth: bandWidth,
      }).lines,
    [bandWidth, centerlinePath, overlayGeometry],
  );
  const viewportOverlayGeometry = useMemo<CprOverlayGeometry | undefined>(() => {
    if (!overlayGeometry?.perpendicularLines?.length) return overlayGeometry;
    return {
      ...overlayGeometry,
      perpendicularLines: overlayGeometry.perpendicularLines.map((line) => {
        const start = imageToSvgPoint(line.start);
        const end = imageToSvgPoint(line.end);
        return {
          ...line,
          start,
          end,
        };
      }),
    };
  }, [overlayGeometry, viewportContext]);
  const perpendicularLines = useMemo(
    () =>
      buildCprOverlayPerpendicularLines({
        centerlinePath: displayCenterlinePath,
        overlayGeometry: viewportOverlayGeometry,
        fallbackBandWidth: bandWidth,
      }).lines,
    [bandWidth, displayCenterlinePath, viewportOverlayGeometry],
  );
  const outlineLines = useMemo(
    () =>
      (overlayGeometry?.outlineLines ?? [])
        .map((line) => ({
          ...line,
          points: line.points.map((point) => imageToSvgPoint(point)),
        }))
        .filter((line) => line.points.length >= 2),
    [overlayGeometry, viewportContext],
  );
  const polygonPoints = useMemo(
    () =>
      getPolyPoints({
        pickPoints: displayPickPointIndexes.map((index) =>
          nearestCprPathArrayIndex(displayCenterlinePath, index),
        ),
        perpendicularLines,
        numPairs,
      }).map(smoothMarkerAreaPolygon),
    [displayCenterlinePath, displayPickPointIndexes, numPairs, perpendicularLines],
  );
  const markerLines = useMemo(
    () =>
      displayPickPointIndexes
        .map((index, which) => {
          const pathIndex = nearestCprPathArrayIndex(
            displayCenterlinePath,
            index,
          );
          return {
            which,
            line: perpendicularLines[pathIndex],
          };
        })
        .filter(
          (
            item,
          ): item is {
            which: number;
            line: PerpendicularLine;
          } => !!item.line,
        ),
    [displayCenterlinePath, displayPickPointIndexes, perpendicularLines],
  );
  const stackMarkerLines = useMemo(
    () =>
      (stackMarkerIndexes ?? [])
        .map((index, markerIndex, source) => {
          const pathIndex = nearestCprPathArrayIndex(displayCenterlinePath, index);
          const isFirst = markerIndex === 0;
          const isLast = markerIndex === source.length - 1;
          return {
            markerIndex,
            stateWhich: isFirst ? 0 : isLast ? 2 : null,
            dashed: !isFirst && !isLast,
            line: perpendicularLines[pathIndex],
          };
        })
        .filter(
          (
            item,
          ): item is {
            markerIndex: number;
            stateWhich: number | null;
            dashed: boolean;
            line: PerpendicularLine;
          } => !!item.line,
        ),
    [displayCenterlinePath, perpendicularLines, stackMarkerIndexes],
  );
  const guidelines =
    branchId !== undefined && !isAorticMode && config.branchArea.size > 0
      ? config.branchArea.get(branchId)
      : null;
  const stenosisarea =
    analysisType === 'stenosis' && branchId !== undefined
      ? config.stenosisLesionInfo[branchId]
      : [];
  const israrea = analysisType === 'isr' ? config.isr_data : [];
  const plaqueBranch = config.plaqueStatus.find(
    (item: any) => item.vesselIndex === branchId,
  );
  const lesionarea =
    analysisType === 'plaque' && config.plaqueStatus.length > 0
      ? plaqueBranch?.lesions
      : null;
  const toViewportPoint = (event: PointerEvent | React.PointerEvent) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    const svgPoint = point.matrixTransform(svg.getScreenCTM()?.inverse());
    return { x: svgPoint.x, y: svgPoint.y };
  };

  const buildPickedPoint = (index: number): PickPoints => {
    const node = centerlineData
      ? handleNodeChange(centerlineData, index)
      : null;
    if (node) {
      return {
        position: node.position,
        normal: node.normal,
        index,
      };
    }

    const imageAsset = getCrossSectionImageAsset(
      imageManifest,
      branchId,
      index,
    );
    const assetPickedPoint = buildPickPointFromCrossSectionAsset(
      imageAsset,
      index,
    );
    if (
      Array.isArray((assetPickedPoint as any).position) &&
      Array.isArray((assetPickedPoint as any).normal)
    ) {
      return assetPickedPoint;
    }

    const manifestPoint = toMarkerPoint(centerlinePath, index);
    if (manifestPoint?.position && manifestPoint.normal) {
      return {
        position: manifestPoint.position,
        normal: manifestPoint.normal,
        index,
      };
    }

    return { index };
  };

  const clampCenterlineIndex = (index: number) => {
    if (isAorticMode || !centerlineData?.getPoints) {
      const pathIndexes = centerlinePath
        .map(getPathPointCenterlineIndex)
        .filter((value) => Number.isFinite(value));
      if (!pathIndexes.length) return index;
      return Math.max(
        Math.min(...pathIndexes),
        Math.min(
          Math.max(...pathIndexes),
          index,
        ),
      );
    }
    const pointCount = centerlineData.getPoints().getNumberOfPoints?.() ?? 0;
    return Math.max(
      config.coronary_front_expand,
      Math.min(pointCount - config.coronary_back_expand - 1, index),
    );
  };

  const getCrossSectionRendererIdForStateWhich = (which: number) => {
    if (!isStackMarkerMode) return `CrossSection.${mainIndex}.${which}`;
    if (which === 0) return `CrossSection.${mainIndex}.0`;
    if (which === 2) return `CrossSection.${mainIndex}.2`;
    return `CrossSection.${mainIndex}.${which}`;
  };

  const scheduleCrossSectionMarkerPreview = (
    which: number,
    nextIndex: number,
  ) => {
    const start = cprSyncNow();
    scheduleRender(getCrossSectionRendererIdForStateWhich(which), {
      type: 'markerMove',
      which,
      newIndex: nextIndex,
      sentAt: start,
    });
  };

  const previewMarkerIndex = (
    which: number,
    nextIndex: number,
  ) => {
    const nextIndexes = pickPointIndexesRef.current.slice();
    nextIndexes[which] = nextIndex;
    if (isServerStackMarkerMode && (which === 0 || which === 2)) {
      const hiddenMiddleIndex = resolveHiddenStackMiddleIndex(nextIndexes);
      if (hiddenMiddleIndex !== null && Number.isFinite(hiddenMiddleIndex)) {
        nextIndexes[1] = hiddenMiddleIndex;
      }
    }
    pickPointIndexesRef.current = nextIndexes;
    scheduleLivePickPointIndexes(nextIndexes);
    scheduleCrossSectionMarkerPreview(which, nextIndex);

    const pickedPoint = buildPickedPoint(nextIndex);
    const oppositeMode = mode === 'stretched' ? 'straightened' : 'stretched';
    scheduleRender(`CPR.${mainIndex}.${oppositeMode}`, {
      type: 'markerMove',
      which,
      newIndices: nextIndex,
      newPickedPoints: [pickedPoint],
    });

    if (isAorticMode) return;

    if (hasWorldPickPoint(pickedPoint)) {
      scheduleRender('Surface3D', {
        type: 'markerMove',
        mainIndex,
        which,
        newPickedPoints: [pickedPoint],
      });
      scheduleRender('VR', {
        type: 'markerMove',
        mainIndex,
        which,
        newPickedPoints: [pickedPoint],
      });
    }

    const localIndex = nextIndex - config.coronary_front_expand;
    scheduleRender('Report', {
      type: 'markerMove',
      mainIndex,
      which,
      newIndex: localIndex,
    });
    scheduleRender(analysisType === 'fai' ? `CPR-${mainIndex}-20` : 'Graph', {
      type: 'markerMove',
      mainIndex,
      which,
      newIndex: localIndex,
    });
  };

  const resolveHiddenStackMiddleIndex = (indexes: number[]) => {
    if (!isServerStackMarkerMode || indexes.length < 3) return null;

    const proximalIndex = Number(indexes[0]);
    const distalIndex = Number(indexes[2]);
    if (!Number.isFinite(proximalIndex) || !Number.isFinite(distalIndex)) {
      return null;
    }

    return clampMiddleIndexBetweenBounds(
      (proximalIndex + distalIndex) / 2,
      proximalIndex,
      distalIndex,
    );
  };

  const syncHiddenStackMiddleIndex = (indexes: number[]) => {
    const middleIndex = resolveHiddenStackMiddleIndex(indexes);
    if (middleIndex === null || !Number.isFinite(middleIndex)) return;

    const nextIndexes = indexes.slice();
    nextIndexes[1] = middleIndex;
    pickPointIndexesRef.current = nextIndexes;

    const middlePickedPoint = buildPickedPoint(middleIndex);
    updateCPRState({
      indexedState: {
        mainIndex: {
          branchId,
          index: 1,
        },
        pickPoint: middlePickedPoint,
      },
    });

    if (!isAorticMode && hasWorldPickPoint(middlePickedPoint)) {
      scheduleRender('Surface3D', {
        type: 'markerMove',
        mainIndex,
        which: 1,
        newPickedPoints: [middlePickedPoint],
      });
      scheduleRender('VR', {
        type: 'markerMove',
        mainIndex,
        which: 1,
        newPickedPoints: [middlePickedPoint],
      });
    }
  };

  const updateMarkerIndex = (which: number, nextIndex: number) => {
    const pickedPoint = buildPickedPoint(nextIndex);
    updateCPRState({
      indexedState: {
        mainIndex: {
          branchId,
          index: which,
        },
        pickPoint: pickedPoint,
      },
    });

    const oppositeMode = mode === 'stretched' ? 'straightened' : 'stretched';
    scheduleRender(`CPR.${mainIndex}.${oppositeMode}`, {
      type: 'markerMove',
      which,
      newIndices: nextIndex,
      newPickedPoints: [pickedPoint],
    });
    scheduleRender(getCrossSectionRendererIdForStateWhich(which), {
      type: 'markerMoveDone',
      which,
      newIndex: nextIndex,
      sentAt: cprSyncNow(),
    });

    if (isAorticMode) {
      setData({ aorta_current_index: nextIndex });
      if (hasWorldPickPoint(pickedPoint)) {
        scheduleRender('Axial', {
          type: 'focus',
          world: pickedPoint.position,
          mode: 'cpr',
          request: true,
        });
        scheduleRender('Surface3D', {
          type: 'aorta_node',
          world: pickedPoint.position,
          normal: pickedPoint.normal,
        });
      }
      return;
    }

    if (hasWorldPickPoint(pickedPoint)) {
      scheduleRender('Axial', {
        type: 'focus',
        world: pickedPoint.position,
        mode: 'cpr',
        request: true,
      });
      scheduleRender('Surface3D', {
        type: 'markerMove',
        mainIndex,
        which,
        newPickedPoints: [pickedPoint],
      });
      scheduleRender('VR', {
        type: 'markerMove',
        mainIndex,
        which,
        newPickedPoints: [pickedPoint],
      });
    }
    scheduleRender('Report', {
      type: 'markerMove',
      mainIndex,
      which,
      newIndex: nextIndex - config.coronary_front_expand,
    });
    scheduleRender(analysisType === 'fai' ? `CPR-${mainIndex}-20` : 'Graph', {
      type: 'markerMove',
      mainIndex,
      which,
      newIndex: nextIndex - config.coronary_front_expand,
    });

    if (isServerStackMarkerMode && (which === 0 || which === 2)) {
      syncHiddenStackMiddleIndex(pickPointIndexesRef.current);
    }
  };

  const handlePointerMove = (event: PointerEvent) => {
    const which = dragIndexRef.current;
    if (which === null) return;
    const svgPoint = toViewportPoint(event);
    if (!svgPoint) return;

    const nearestIndex = nearestCprPathIndexByPoint(
      displayCenterlinePath,
      {
        x: svgPoint.x,
        y: svgPoint.y,
      },
      indexPickMode,
    );
    if (nearestIndex === null) return;

    const previousIndex =
      isStackMarkerMode && which === 2
        ? pickPointIndexesRef.current[0]
        : which === 0
          ? Number.NEGATIVE_INFINITY
          : pickPointIndexesRef.current[which - 1];
    const nextIndex =
      isStackMarkerMode && which === 0
        ? pickPointIndexesRef.current[2]
        : which === pickPointIndexesRef.current.length - 1
          ? Number.POSITIVE_INFINITY
          : pickPointIndexesRef.current[which + 1];

    if (!(previousIndex < nearestIndex && nearestIndex < nextIndex)) return;

    const clampedIndex = clampCenterlineIndex(nearestIndex);

    if (pickPointIndexesRef.current[which] === clampedIndex) return;
    previewMarkerIndex(which, clampedIndex);
  };

  const handlePointerUp = () => {
    const which = dragIndexRef.current;
    if (which !== null) {
      flushLivePickPointIndexes();
      updateMarkerIndex(which, pickPointIndexesRef.current[which]);
      window.setTimeout(() => setLivePickPointIndexes(null), 0);
    }
    dragIndexRef.current = null;
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', handlePointerUp);
  };

  const previewMarkerGroupIndices = (
    indices: number[],
    nextIndices: number[],
  ) => {
    if (!indices.length || indices.length !== nextIndices.length) return;
    const nextAllIndices = pickPointIndexesRef.current.slice();
    indices.forEach((which, offset) => {
      nextAllIndices[which] = nextIndices[offset];
    });
    pickPointIndexesRef.current = nextAllIndices;

    scheduleLivePickPointIndexes(nextAllIndices);

    // Server canvas CrossSection is now light enough to test real-time group
    // sync. Keep legacy VTK mode on the previous commit-only path.
    if (!isServerImageMode) return;

    const start = cprSyncNow();
    const allPickedPoints = nextAllIndices.map((index) =>
      buildPickedPoint(index),
    );
    indices.forEach((which, offset) => {
      scheduleRender(getCrossSectionRendererIdForStateWhich(which), {
        type: 'markerMove',
        which,
        newIndex: nextIndices[offset],
        markerGroupPreview: true,
        sentAt: start,
      });
    });

    const oppositeMode = mode === 'stretched' ? 'straightened' : 'stretched';
    scheduleRender(`CPR.${mainIndex}.${oppositeMode}`, {
      type: 'polygonMove',
      realIdx: 0,
      newPickedPoints: allPickedPoints,
    });

    if (isAorticMode) {
      return;
    }

    const linkedPickedPoints = indices
      .map((which, offset) => {
        const pickedPoint = buildPickedPoint(nextIndices[offset]);
        if (!hasWorldPickPoint(pickedPoint)) return null;
        return {
          ...pickedPoint,
          index: nextIndices[offset] - config.coronary_front_expand,
          which,
        };
      })
      .filter(Boolean);

    if (linkedPickedPoints.length === indices.length) {
      scheduleRender('Surface3D', {
        type: 'polygonMove',
        mainIndex,
        which: 'total',
        newPickedPoints: linkedPickedPoints,
      });
      scheduleRender('VR', {
        type: 'polygonMove',
        mainIndex,
        which: 'total',
        newPickedPoints: linkedPickedPoints,
      });
    }
    scheduleRender('Report', {
      type: 'polygonMove',
      mainIndex,
      which: 'total',
      newPickedPoints: linkedPickedPoints,
    });
    scheduleRender(analysisType === 'fai' ? `CPR-${mainIndex}-20` : 'Graph', {
      type: 'polygonMove',
      mainIndex,
      which: 'total',
      newPickedPoints: linkedPickedPoints,
    });
  };

  const commitMarkerGroupIndices = (indices: number[]) => {
    if (!indices.length) return;
    const nextAllIndices = pickPointIndexesRef.current.slice();
    if (
      isServerStackMarkerMode &&
      (indices.includes(0) || indices.includes(2))
    ) {
      const middleIndex = resolveHiddenStackMiddleIndex(nextAllIndices);
      if (middleIndex !== null && Number.isFinite(middleIndex)) {
        nextAllIndices[1] = middleIndex;
        pickPointIndexesRef.current = nextAllIndices;
      }
    }
    const nextIndices = indices.map((which) => nextAllIndices[which]);
    const allPickedPoints = nextAllIndices.map((index) =>
      buildPickedPoint(index),
    );
    updateCPRState({
      indexedStates: {
        [branchId]: allPickedPoints,
      },
    });

    const oppositeMode = mode === 'stretched' ? 'straightened' : 'stretched';
    scheduleRender(`CPR.${mainIndex}.${oppositeMode}`, {
      type: 'polygonMove',
      realIdx: 0,
      newPickedPoints: allPickedPoints,
    });

    const commitCrossSectionSentAt = cprSyncNow();
    indices.forEach((which, offset) => {
      scheduleRender(getCrossSectionRendererIdForStateWhich(which), {
        type: 'markerMoveDone',
        which,
        newIndex: nextIndices[offset],
        sentAt: commitCrossSectionSentAt,
      });
    });

    const focusOffset = Math.floor(indices.length / 2);
    const focusIndex = nextIndices[focusOffset];
    const focusPickedPoint =
      focusIndex !== undefined ? buildPickedPoint(focusIndex) : null;

    if (isAorticMode) {
      setData({ aorta_current_index: focusIndex });
      if (focusPickedPoint && hasWorldPickPoint(focusPickedPoint)) {
        scheduleRender('Axial', {
          type: 'focus',
          world: focusPickedPoint.position,
          mode: 'cpr',
          request: true,
        });
        scheduleRender('Surface3D', {
          type: 'aorta_node',
          world: focusPickedPoint.position,
          normal: focusPickedPoint.normal,
        });
      }
      return;
    }
    if (focusPickedPoint && hasWorldPickPoint(focusPickedPoint)) {
      scheduleRender('Axial', {
        type: 'focus',
        world: focusPickedPoint.position,
        mode: 'cpr',
        request: true,
      });
    }
    const linkedPickedPoints = indices
      .map((which, offset) => {
        const pickedPoint = buildPickedPoint(nextIndices[offset]);
        if (!hasWorldPickPoint(pickedPoint)) return null;
        return {
          ...pickedPoint,
          index: nextIndices[offset] - config.coronary_front_expand,
          which,
        };
      })
      .filter(Boolean);

    if (linkedPickedPoints.length === indices.length) {
      scheduleRender('Surface3D', {
        type: 'polygonMove',
        mainIndex,
        which: 'total',
        newPickedPoints: linkedPickedPoints,
      });
      scheduleRender('VR', {
        type: 'polygonMove',
        mainIndex,
        which: 'total',
        newPickedPoints: linkedPickedPoints,
      });
    }
    scheduleRender('Report', {
      type: 'polygonMove',
      mainIndex,
      which: 'total',
      newPickedPoints: linkedPickedPoints,
    });
    scheduleRender(analysisType === 'fai' ? `CPR-${mainIndex}-20` : 'Graph', {
      type: 'polygonMove',
      mainIndex,
      which: 'total',
      newPickedPoints: linkedPickedPoints,
    });

    if (
      isServerStackMarkerMode &&
      (indices.includes(0) || indices.includes(2))
    ) {
      syncHiddenStackMiddleIndex(pickPointIndexesRef.current);
    }
  };

  const handlePolygonPointerMove = (event: PointerEvent) => {
    const dragGroup = dragGroupRef.current;
    if (!dragGroup) return;
    const svgPoint = toViewportPoint(event);
    if (!svgPoint) return;
    const nearestPathArrayIndex = nearestCprPathArrayIndexByPoint(
      displayCenterlinePath,
      {
        x: svgPoint.x,
        y: svgPoint.y,
      },
      indexPickMode,
    );
    if (
      nearestPathArrayIndex === null ||
      nearestPathArrayIndex === dragGroup.startPathArrayIndex
    ) {
      return;
    }

    const deltaPathIndex = nearestPathArrayIndex - dragGroup.startPathArrayIndex;
    const nextIndices = dragGroup.indices.map((which) => {
      const currentPathIndex = nearestCprPathArrayIndex(
        displayCenterlinePath,
        pickPointIndexesRef.current[which],
      );
      const nextPathPoint =
        displayCenterlinePath[
          Math.max(
            0,
            Math.min(
              displayCenterlinePath.length - 1,
              currentPathIndex + deltaPathIndex,
            ),
          )
        ];
      return clampCenterlineIndex(
        nextPathPoint
          ? getPathPointCenterlineIndex(nextPathPoint)
          : pickPointIndexesRef.current[which],
      );
    });
    const previousIndex =
      dragGroup.indices[0] === 0
        ? Number.NEGATIVE_INFINITY
        : pickPointIndexesRef.current[dragGroup.indices[0] - 1];
    const nextIndex =
      dragGroup.indices[dragGroup.indices.length - 1] ===
      pickPointIndexesRef.current.length - 1
        ? Number.POSITIVE_INFINITY
        : pickPointIndexesRef.current[
            dragGroup.indices[dragGroup.indices.length - 1] + 1
          ];
    if (
      !(
        previousIndex < nextIndices[0] &&
        nextIndices[nextIndices.length - 1] < nextIndex
      )
    ) {
      return;
    }
    if (
      nextIndices.every(
        (next, offset) =>
          next === pickPointIndexesRef.current[dragGroup.indices[offset]],
      )
    ) {
      return;
    }

    previewMarkerGroupIndices(
      dragGroup.indices,
      nextIndices,
    );
    dragGroup.startPathArrayIndex = nearestPathArrayIndex;
  };

  const handlePolygonPointerUp = () => {
    const dragGroup = dragGroupRef.current;
    if (dragGroup) {
      flushLivePickPointIndexes();
      commitMarkerGroupIndices(dragGroup.indices);
      window.setTimeout(() => setLivePickPointIndexes(null), 0);
    }
    dragGroupRef.current = null;
    window.removeEventListener('pointermove', handlePolygonPointerMove);
    window.removeEventListener('pointerup', handlePolygonPointerUp);
  };

  const handlePolygonPointerDown = (
    groupIndex: number,
    event: React.PointerEvent<SVGPolygonElement>,
  ) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const svgPoint = toViewportPoint(event);
    const startPathArrayIndex = svgPoint
      ? nearestCprPathArrayIndexByPoint(
          displayCenterlinePath,
          {
            x: svgPoint.x,
            y: svgPoint.y,
          },
          indexPickMode,
        )
      : null;
    if (startPathArrayIndex === null) return;
    dragGroupRef.current = {
      indices: Array.from(
        { length: numPairs },
        (_value, offset) => groupIndex * numPairs + offset,
      ).filter((which) => which < pickPointIndexesRef.current.length),
      startPathArrayIndex,
    };
    window.addEventListener('pointermove', handlePolygonPointerMove, {
      passive: true,
    });
    window.addEventListener('pointerup', handlePolygonPointerUp);
  };

  const handlePointerDown = (
    which: number,
    event: React.PointerEvent<SVGLineElement>,
  ) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    dragIndexRef.current = which;
    window.addEventListener('pointermove', handlePointerMove, {
      passive: true,
    });
    window.addEventListener('pointerup', handlePointerUp);
  };

  const renderIndexBand = (
    key: string,
    indexRange: [number, number],
    linecolor: string,
    fillcolor: string,
  ) => {
    const overlay = buildCprIndexBandOverlay({
      centerlinePath,
      indexRange,
      width,
      perpendicularLines: imagePerpendicularLines,
      overlayGeometry,
      bandKey: key,
    });
    if (!overlay) return null;

    return (
      <g key={key} pointerEvents="none">
        <polygon
          points={
            overlay.coordinateSpace === 'image'
              ? toSvgPolygon(overlay.polygonPoints)
              : overlay.polygonPoints
          }
          fill={fillcolor}
          fillOpacity={0.1}
        />
        {overlay.boundaryLines.map((line, index) =>
          (() => {
            const screenLine =
              overlay.coordinateSpace === 'image' ? lineToSvg(line) : line;
            return (
              <line
                key={`${key}-line-${index}`}
                x1={screenLine.startX}
                y1={screenLine.startY}
                x2={screenLine.endX}
                y2={screenLine.endY}
                stroke={linecolor}
                strokeWidth={1}
              />
            );
          })(),
        )}
      </g>
    );
  };

  return (
    <svg
      ref={svgRef}
      className="server-image-marker-overlay"
      viewBox={`0 0 ${viewportWidth} ${viewportHeight}`}
      preserveAspectRatio="none"
    >
      {shouldRenderPlaqueOutline &&
        !hidePlaqueOutline &&
        label.plaqueoutline !== false &&
        outlineLines.map((line, index) => {
          const d = pointsToPathD(line.points, line.closed);
          if (!d) return null;
          return (
            <path
              key={`front-data-outline-${line.key ?? index}`}
              d={d}
              fill="none"
              stroke={outlineStrokeForType(line.type, config)}
              strokeWidth={1.2}
              strokeOpacity={0.95}
              vectorEffect="non-scaling-stroke"
              pointerEvents="none"
            />
          );
        })}
      {guidelines &&
        label.guideline &&
        guidelines.map(([label, rawIndex]: [string, number], index: number) => {
          const pathIndex = nearestCprPathArrayIndex(
            displayCenterlinePath,
            rawIndex + config.coronary_front_expand,
          );
          const point = displayCenterlinePath[pathIndex];
          if (!point) return null;
          const y = point.y;
          return (
            <g key={`guide-${index}`} pointerEvents="none">
              <line
                x1={0}
                y1={y}
                x2={viewportWidth}
                y2={y}
                stroke="red"
                strokeWidth={1.5}
                strokeDasharray="4 3"
              />
              <text
                x={viewportWidth - 6}
                y={y - 6}
                fill="red"
                fontSize={12}
                fontWeight={700}
                textAnchor="end"
                alignmentBaseline="baseline"
                style={{
                  paintOrder: 'stroke',
                  stroke: 'rgba(0,0,0,0.6)',
                  strokeWidth: 3,
                }}
              >
                {label}
              </text>
            </g>
          );
        })}
      {stenosisarea &&
        label.stenosisline &&
        stenosisarea.map((data: any, index: number) => {
          const [start, _mid, end, value] = data;
          if (value === 0) return null;
          const color =
            value <= 0.24
              ? config.colors.autoLesion[0]
              : value <= 0.49
                ? config.colors.autoLesion[1]
                : value <= 0.69
                  ? config.colors.autoLesion[2]
                  : config.colors.autoLesion[3];
          return renderIndexBand(
            `stenosis-${index}`,
            [
              start + config.coronary_front_expand,
              end + config.coronary_front_expand,
            ],
            color,
            color,
          );
        })}
      {israrea &&
        label.isrline &&
        branchId !== undefined &&
        israrea.map(
          (
            value: Map<number, { index: [number, number] }> | null,
            index: number,
          ) => {
            if (value === null) return null;
            const data = value.get(branchId);
            if (!data) return null;
            return renderIndexBand(
              `isr-${index}`,
              data.index,
              config.colors.isrcontent[index],
              config.colors.isrcontent[index],
            );
          },
        )}
      {lesionarea &&
        !hidePlaqueLesion &&
        label.lesionline &&
        lesionarea.map((data: any, index: number) => {
          if (index > 2) return null;
          return renderIndexBand(
            `plaque-${index}`,
            [data.value[0], data.value[2]],
            config.colors.isrcontent[index],
            config.colors.isrcontent[index],
          );
        })}
      {label.markerline &&
        polygonPoints.map((polygonPoint, index) => (
          <polygon
            key={`marker-area-${index}`}
            points={polygonPoint}
            fill={
              hoveredPolygonIndex === index
                ? '#FFD700'
                : config.colors.MarkerArea
            }
            fillOpacity={hoveredPolygonIndex === index ? 0.3 : 0.1}
            stroke="none"
            strokeWidth={0}
            onPointerDown={(event) => handlePolygonPointerDown(index, event)}
            onPointerOver={() => setHoveredPolygonIndex(index)}
            onPointerOut={() => setHoveredPolygonIndex(null)}
            style={{
              cursor: 'move',
              pointerEvents: 'auto',
            }}
          />
        ))}
      {label.markerline &&
        isStackMarkerMode &&
        stackMarkerLines.map(({ markerIndex, stateWhich, dashed, line }) => {
          const screenLine = line;
          const midpoint = lineMidpoint(screenLine);
          const { offsetX, offsetY } = lineUnit(screenLine);
          const gap = dashed || isAorticMode ? 0 : 5;
          const stroke =
            stateWhich === 0
              ? config.colors.Proximal
              : stateWhich === 2
                ? config.colors.Distal
                : config.colors.Stenosis;

          return (
            <g key={`stack-marker-line-${markerIndex}`}>
              {stateWhich !== null && (
                <line
                  className="server-image-marker-hit"
                  x1={screenLine.startX - offsetX * gap}
                  y1={screenLine.startY - offsetY * gap}
                  x2={screenLine.endX + offsetX * gap}
                  y2={screenLine.endY + offsetY * gap}
                  strokeWidth={10}
                  onPointerDown={(event) => handlePointerDown(stateWhich, event)}
                />
              )}
              <line
                x1={screenLine.startX}
                y1={screenLine.startY}
                x2={dashed ? screenLine.endX : midpoint.x - offsetX * gap}
                y2={dashed ? screenLine.endY : midpoint.y - offsetY * gap}
                stroke={stroke}
                strokeWidth={dashed ? 1.2 : 1.5}
                strokeOpacity={dashed ? 0.85 : 1}
                strokeDasharray={dashed ? "4 4" : undefined}
                pointerEvents="none"
              />
              {!dashed && (
                <line
                  x1={midpoint.x + offsetX * gap}
                  y1={midpoint.y + offsetY * gap}
                  x2={screenLine.endX}
                  y2={screenLine.endY}
                  stroke={stroke}
                  strokeWidth={1.5}
                  pointerEvents="none"
                />
              )}
            </g>
          );
        })}
      {label.markerline &&
        !isStackMarkerMode &&
        markerLines.map(({ which, line }) => {
          const screenLine = line;
          const midpoint = lineMidpoint(screenLine);
          const { offsetX, offsetY } = lineUnit(screenLine);
          const gap = isAorticMode ? 0 : 5;
          const style = markerLineStyle({
            markerOffset: which,
            numPairs,
            config,
          });

          return (
            <g key={`marker-line-${which}`}>
              <line
                className="server-image-marker-hit"
                x1={screenLine.startX - offsetX * gap}
                y1={screenLine.startY - offsetY * gap}
                x2={screenLine.endX + offsetX * gap}
                y2={screenLine.endY + offsetY * gap}
                strokeWidth={style.hitWidth}
                onPointerDown={(event) => handlePointerDown(which, event)}
              />
              <line
                className="server-image-marker-hit-wing"
                x1={screenLine.startX}
                y1={screenLine.startY}
                x2={midpoint.x - offsetX * gap}
                y2={midpoint.y - offsetY * gap}
                stroke={style.stroke}
                strokeWidth={1.5}
                pointerEvents="none"
              />
              <line
                className="server-image-marker-hit-wing"
                x1={midpoint.x + offsetX * gap}
                y1={midpoint.y + offsetY * gap}
                x2={screenLine.endX}
                y2={screenLine.endY}
                stroke={style.stroke}
                strokeWidth={1.5}
                pointerEvents="none"
              />
            </g>
          );
        })}
    </svg>
  );
}
